/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { isArray } from 'lodash';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { hooks as commonHooks, types as commonTypes } from '@sitebuilder/common';
import {
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
    isLocalStorageAccessible,
} from '../utils';
import config from './config.json';
import Filters from './Filters';
import MobileFilters from './MobileFilters';
import fundListStyles, { themeFundListStyles } from './FundList.styles';
import Table from './Table';
import { testData } from './sampleData';
import preparePnPViewModel, { Config, View } from './utils';
import ViewDropdown from './ViewDropdown';
import ViewTabs from './ViewTabs';
import PlaceholderContainer from './PlaceholderContainer';
import EmailContainer from './EmailContainer';
import CopyContainer from './CopyContainer';
import 'intersection-observer';

interface WidgetProperties extends commonTypes.WidgetPropertiesBase {
    typographyClass: string;
    disableTabStaticData: boolean;
}

const FundList = (props: commonTypes.WidgetInstanceProps) => {
    const widgetProperties =
        commonHooks.useWidgetProperties<WidgetProperties & Config>(props.widgetInstanceId) ||
        props.properties;
    const widgetInstance = commonHooks.useWidgetInstance(props.widgetInstanceId);
    const { disableTabStaticData } =
        commonHooks.useWidgetProperties<WidgetProperties>('root') || {};
    const {
        styles,
        views,
        searchLabel,
        searchTitle,
        hideAllFilters,
        filters,
        grouping,
        firstColumnOptions,
        hideTabHeader,
        fundUrlMapping,
        fundUrlBase,
        fundPdfUrlBase,
        fundPdfName,
        constantDataPoints,
        selectedListStorageName,
        ...rest
    } = widgetProperties || props;

    const location = useLocation();
    const i18n = commonHooks.useLocalization();
    const {
        setters: { setSelectedList },
    } = commonHooks.useAppContext();

    const search = location.search;
    const params = new URLSearchParams(search);
    const fundCodeList = params.get('fundCodeList');
    const theme = props?.data?.themeSelected || props?.theme;
    let fundListData = props.data?.webProfiles;

    if (!fundListData) {
        fundListData = testData.webProfiles;
    }

    const {
        data: processedData,
        views: processedViews,
        filters: processedFilters,
        grouping: processedGrouping,
    } = preparePnPViewModel(
        fundListData as any,
        {
            views: views || config.views,
            filters: filters || config.filters,
            grouping: grouping?.propertyId ? grouping : config.grouping,
            firstColumnOptions,
            constantDataPoints: constantDataPoints || [],
        },
        i18n
    );

    const [filterState, setFilterState] = useState<Record<string, string>>({});
    const [searchTerm, setSearchTerm] = useState('');
    const [disableCheckbox, setDisableCheckbox] = useState(false);
    const [sortState, setSortState] = useState<{
        sortProperty: string;
        isAscending: boolean;
    } | null>(null);
    const [currentViewId, setCurrentViewId] = useState<string | null>(null);
    const [checkList, setCheckList] = useState<string[]>([]);
    const [favorites, setFavorites] = useState<any>([]);
    const [filterFavorites, setFilterFavorites] = useState<boolean>(false);
    const [isInformationFolderVisible, setIsInformationFolderVisible] = useState<boolean>(false);

    let transformData = {
        data: processedData,
        filters: processedFilters,
        locale: props.data?.locale,
        currentViewId,
        fundListData,
        documents: props.data?.documents,
    };

    try {
        // eslint-disable-next-line no-new-func
        const transformFunction = Function(`${rest?.transformFunction}`)();
        const transformedData = transformFunction && transformFunction(transformData);

        if (transformedData) {
            transformData = { ...transformData, ...transformedData };
        }
    } catch (e) {
        console.log(e);
    }

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                let fundListVisible = false;
                let infoFolderVisible = false;
                entries.forEach((entry) => {
                    if (entry.target.id === 'fundlistSection') {
                        fundListVisible = entry.isIntersecting;
                    } else if (entry.target.id === 'informationFolder') {
                        const fundListTable = document.querySelector(
                            '#fund-list-table'
                        ) as HTMLTableElement;
                        infoFolderVisible = entry.isIntersecting && fundListTable.rows.length > 10;
                    }
                });

                setIsInformationFolderVisible(infoFolderVisible && !fundListVisible);
            },
            { threshold: 0 }
        );

        const fundlistSection = document.querySelector('#fundlistSection') as HTMLElement;
        const informationFolderRef = document.querySelector('#informationFolder') as HTMLElement;
        if (informationFolderRef && fundlistSection) {
            observer.observe(informationFolderRef);
            observer.observe(fundlistSection);

            return () => {
                observer.unobserve(informationFolderRef);
                observer.unobserve(fundlistSection);
            };
        }
    }, []);

    useEffect(() => {
        const currentUrl = new URL(window.location.href);
        const currentUrlParams = new URLSearchParams(currentUrl.search);
        const viewQueryParam = currentUrlParams.get('view') || null;

        // Set the view based on query parameter
        setCurrentViewId(viewQueryParam);
    }, []);

    useEffect(() => {
        transformData?.filters?.forEach((f) => {
            const doesFilterStateExist = !!filterState[f.id];

            if (!doesFilterStateExist && f.defaultValue) {
                setFilterState({
                    ...filterState,
                    [f.id]: i18n.format(f.defaultValue, props.data),
                });
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterState, transformData?.filters, props.data]);

    const handleSort = (propertyId: string) => {
        if (sortState && sortState.sortProperty) {
            setSortState({ sortProperty: propertyId, isAscending: !sortState.isAscending });
        } else {
            setSortState({ sortProperty: propertyId, isAscending: true });
        }
    };

    const filterAndSortRows = (processedRows: Record<string, any>[]) => {
        if (filterFavorites) {
            processedRows = processedRows.filter((row) => favorites?.includes(row.entityId));
        }

        // filter rows with funCodeList from url
        if (fundCodeList) {
            const fundCodes = fundCodeList.split('|');
            if (fundCodes && fundCodes.filter((fc) => fc).length > 0)
                /* use the filter function along with some function to check
                if any of the fundcode in fundcodes list are in the rows
                entity id and filter them out */
                processedRows = processedRows.filter((row) =>
                    fundCodes.some((fundCode) => row.entityId.includes(fundCode))
                );
        }

        if (filterState) {
            Object.keys(filterState).forEach((p) => {
                const filterValue = filterState[p];

                if (filterValue && filterValue !== 'all') {
                    processedRows = processedRows.filter((model) => {
                        const modelValue = model[p];
                        return isArray(modelValue)
                            ? modelValue.filter((v) => filterValue === v.value).length
                            : filterValue === modelValue;
                    });
                }
            });
        }

        // == Apply optional fund name/code search ==
        if (searchTerm) {
            let fundCodeProperties: any[] = [];
            // const fundCode

            if (processedViews.find((o) => o.id === 'fundCodes')) {
                fundCodeProperties =
                    processedViews.find((o) => o.id === 'fundCodes')?.columns?.map((c) => c.id) ||
                    [];
            }

            processedRows = processedRows.filter((o) => {
                const matchesFundName = o?.fundName
                    ?.toLowerCase()
                    ?.includes(searchTerm.toLowerCase());

                // Return early if it already matches the fund name
                if (matchesFundName) return matchesFundName;

                // Otherwise do a more expensive search on the fund codes
                const matchesFundCodes = fundCodeProperties?.reduce((memo, s) => {
                    const fundCode = o[`original${s}`] || o[s];

                    return (
                        memo ||
                        (typeof fundCode === 'string' &&
                            fundCode.toLowerCase().includes(searchTerm.toLowerCase()))
                    );
                }, false);

                return matchesFundCodes;
            });
        }

        // == Apply optional sort ==
        if (sortState && sortState.sortProperty) {
            processedRows.sort((a: any, b: any) => {
                let aProp = a[sortState.sortProperty];
                let bProp = b[sortState.sortProperty];

                const sortPropertySplit = sortState.sortProperty.split('.');

                if (sortPropertySplit.length > 1) {
                    aProp = a[sortPropertySplit[0]];
                    bProp = b[sortPropertySplit[0]];

                    aProp = aProp ? aProp[sortPropertySplit[1]] : null;
                    bProp = bProp ? bProp[sortPropertySplit[1]] : null;
                }

                const sortDirectionValue = sortState.isAscending ? 1 : -1;

                if (aProp < bProp) {
                    return -1 * sortDirectionValue;
                } else if (aProp > bProp) {
                    return 1 * sortDirectionValue;
                } else {
                    return 0;
                }
            });
        }

        return processedRows;
    };

    const selectedViewId = currentViewId || processedViews[0]?.id || '';
    const selectedView = processedViews?.find((v) => v.id === selectedViewId);
    const columnGroups = selectedView?.columnGroups;
    const filteredColumns = selectedView?.columns || [];
    const processedRows = filterAndSortRows(transformData?.data);
    const rowUpdateCount = processedRows.length;

    const onClickClearSelection = () => {
        const _isLocalStorageAccessible: boolean = isLocalStorageAccessible();

        if (_isLocalStorageAccessible) {
            // clears the selectedList storage object, removes local storage and disabling checkbox
            localStorage.removeItem('selectedList');
            setSelectedList([]);
            setDisableCheckbox(false);
        }
    };

    return (
        <div
            css={css`
                ${fundListStyles}${theme ? themeFundListStyles : ''}${getTextSchemaProperties(
                    widgetProperties,
                    props?.data?.colorSpace
                )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                    widgetProperties
                )}${styles || ''}
            `}
            className={`fund-list-container ${
                widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
            } ${isInformationFolderVisible ? 'scrolling' : ''}`}
        >
            <div className="sr-only" aria-live="assertive">
                {i18n.translate('widgets_fundList_dataUpdate', { count: rowUpdateCount }) as any}
            </div>
            {!hideAllFilters && (
                <>
                    <Filters
                        config={transformData?.filters}
                        filterState={filterState || {}}
                        setFilters={setFilterState}
                        searchTerm={searchTerm}
                        searchLabel={searchLabel}
                        searchTitle={searchTitle}
                        setSearchTerm={setSearchTerm}
                        dataContext={props.data}
                        isInformationFolderVisible={isInformationFolderVisible}
                    />
                    <MobileFilters
                        config={transformData?.filters}
                        filterState={filterState || {}}
                        setFilters={setFilterState}
                        searchTerm={searchTerm}
                        searchLabel={searchLabel}
                        searchTitle={searchTitle}
                        setSearchTerm={setSearchTerm}
                        dataContext={props.data}
                        isInformationFolderVisible={isInformationFolderVisible}
                    />
                </>
            )}
            <ViewDropdown
                onChange={setCurrentViewId}
                options={processedViews}
                selected={selectedViewId}
                dataContext={props.data}
            />
            <div
                className={`placeholder-functions-wrapper sticky-header ${
                    isInformationFolderVisible ? 'information-visible' : ''
                }`}
            >
                <div>
                    {rest.addPlaceholderAfterFilters && (
                        <PlaceholderContainer
                            dataContext={props.data}
                            widgetInstance={widgetInstance}
                        />
                    )}
                </div>
                <div className="functions-container">
                    {rest.isShowClearSelectionButton && (
                        <button
                            className={'clear-selection-btn'}
                            onClick={() => onClickClearSelection()}
                        >
                            {i18n.format(rest.clearSelectionButtonName, props.data) ||
                                i18n.t('clear_selection')}
                        </button>
                    )}
                    {rest.isShowCopyButton && (
                        <CopyContainer
                            dataContext={props.data}
                            copyBtnTransformFunc={rest.copyBtnTransformFunc}
                            copyBtnDataKey={rest.copyBtnDataKey}
                            copyBtnLocalStorageItemName={rest.copyBtnLocalStorageItemName}
                            copyOnHoverText={rest.copyOnHoverText}
                            copyButtonText={rest.copyButtonText}
                            params={params}
                        />
                    )}
                    {rest.isShowEmailButton && (
                        <EmailContainer
                            dataContext={props.data}
                            emailBtnTransformFunc={rest.emailBtnTransformFunc}
                            emailBtnDataKey={rest.emailBtnDataKey}
                            emailBtnLocalStorageItemName={rest.emailBtnLocalStorageItemName}
                            receiverEmailId={rest.receiverEmailId}
                            emailSubject={rest.emailSubject}
                            emailOnHoverText={rest.emailOnHoverText}
                            emailButtonText={rest.emailButtonText}
                            params={params}
                        />
                    )}
                </div>
            </div>
            <ViewTabs
                onChange={setCurrentViewId}
                options={processedViews}
                selected={selectedViewId}
                dataContext={props.data}
                hideTabHeader={hideTabHeader}
            />

            <Table
                firstColumnOptions={firstColumnOptions}
                columns={filteredColumns}
                columnGroups={columnGroups}
                rows={processedRows}
                sortState={sortState}
                grouping={processedGrouping}
                onToggleSort={handleSort}
                checkList={checkList}
                setCheckList={setCheckList}
                iconShowContent={rest.iconShowContent}
                iconHideContent={rest.iconHideContent}
                useIconForFavoriteColumn={rest.useIconForFavoriteColumn}
                fundUrlMapping={fundUrlMapping}
                fundUrl={rest.fundUrl}
                fundUrlBase={fundUrlBase}
                fundPdfUrlBase={fundPdfUrlBase}
                fundPdfName={fundPdfName}
                favorites={favorites}
                selectedListStorageName={selectedListStorageName}
                selectedListDataId={rest.selectedListDataId}
                maxSelectionList={rest.maxSelectionList}
                selectedListJsonPath={rest.selectedListJsonPath}
                setFavorites={setFavorites}
                filterFavorites={filterFavorites}
                setFilterFavorites={setFilterFavorites}
                dataContext={props.data}
                selectedView={selectedView as View}
                downloadFileOptions={rest.downloadFileOptions}
                disableCheckbox={disableCheckbox}
                setDisableCheckbox={setDisableCheckbox}
                data={props.data}
                isInformationFolderVisible={isInformationFolderVisible}
                disableTabStaticData={disableTabStaticData}
            />
        </div>
    );
};

export default FundList;
